import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Delete,
} from '@mui/icons-material';
import { formatDateLocal } from '../../utils/dateUtils';
import TaskWorkLogDialog from './TaskWorkLogDialog';

/** Format decimal hours as "Xh Ymin" (e.g. 10.1 → "10h 6min"). */
function formatHoursMinutes(decimalHours: number): string {
  const h = Math.floor(decimalHours);
  const min = Math.round((decimalHours - h) * 60);
  if (min > 0) return `${h}h ${min}min`;
  return `${h}h`;
}

interface User {
  id: number;
  username: string;
  email: string;
  role?: string;
}

interface AttendanceEntry {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  checkin: string | null;
  checkout: string | null;
  in_position: string | null;
  out_position: string | null;
  task_hours?: number;
  total_hours?: number;
  log_type?: string;
}

interface DateViewProps {
  users: User[];
  attendance: AttendanceEntry[];
  loading: boolean;
  canManage?: boolean;
  onEdit?: (entry: AttendanceEntry) => void;
  onDelete?: (id: number, logType?: string) => void;
  onCreate?: (user: User, date: Date) => void;
  onDateRangeChange?: (range: { dateFrom: string; dateTo: string }) => void;
}

export default function DateView({ 
  users, 
  attendance, 
  loading, 
  canManage = false,
  onEdit,
  onDelete,
  onCreate,
  onDateRangeChange,
}: DateViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Sort users: by role (System Admin > Sale > Field Worker), then alphabetically
  const sortedUsers = useMemo(() => {
    const rolePriority: Record<string, number> = {
      'SYSTEM ADMIN': 0,
      'SALE': 1,
      'FIELD WORKER': 2,
    };
    return [...users].sort((a, b) => {
      const roleA = rolePriority[a.role?.toUpperCase() || ''] ?? 99;
      const roleB = rolePriority[b.role?.toUpperCase() || ''] ?? 99;
      if (roleA !== roleB) return roleA - roleB;
      return (a.username || a.email).localeCompare(b.username || b.email);
    });
  }, [users]);

  const [taskLogDialogOpen, setTaskLogDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Get dates for the current week (7 days)
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    // Calculate days to subtract to get to Sunday (week starts on Sunday)
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentDate]);

  // Notify parent of date range change
  useEffect(() => {
    if (onDateRangeChange && weekDates.length > 0) {
      // Use exact dates being displayed (no buffer)
      const dateFrom = formatDateLocal(weekDates[0]);
      const dateTo = formatDateLocal(weekDates[6]);
      onDateRangeChange({ dateFrom, dateTo });
    }
  }, [weekDates, onDateRangeChange]);

  // Create a map of attendance by user_id and date
  const attendanceMap = useMemo(() => {
    const map = new Map<string, AttendanceEntry>();
    attendance.forEach((entry) => {
      if (entry.date && entry.user_id) {
        // Normalize date string to YYYY-MM-DD format (extract first 10 characters)
        const normalizedDate = typeof entry.date === 'string' 
          ? entry.date.trim().substring(0, 10)
          : entry.date;
        // Only use if it matches YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
          const key = `${entry.user_id}-${normalizedDate}`;
          map.set(key, entry);
        }
      }
    });
    return map;
  }, [attendance]);

  // Calculate hours worked
  const calculateHours = (checkin: string | null, checkout: string | null): number | null => {
    if (!checkin || !checkout) return null;
    try {
      const checkinTime = new Date(checkin);
      const checkoutTime = new Date(checkout);
      const diffMs = checkoutTime.getTime() - checkinTime.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      return Math.round(diffHours * 100) / 100;
    } catch {
      return null;
    }
  };

  // Get attendance status for a user on a specific date
  const getAttendanceStatus = (userId: number, date: Date): { status: string; hours: number | null; taskHours: number; entry: AttendanceEntry | null } => {
    const dateStr = formatDateLocal(date);
    // Ensure dateStr is exactly YYYY-MM-DD format
    const normalizedDateStr = dateStr.substring(0, 10);
    const key = `${userId}-${normalizedDateStr}`;
    const entry = attendanceMap.get(key);

    if (!entry) {
      return { status: 'absent', hours: null, taskHours: 0, entry: null };
    }

    const taskHours = entry.task_hours || 0;

    // Use total_hours from backend if available (calculated from individual records)
    // Otherwise fall back to calculating from checkin/checkout
    let hours: number | null = null;
    if (entry.total_hours !== undefined && entry.total_hours !== null) {
      hours = entry.total_hours;
    } else if (entry.checkin && entry.checkout) {
      hours = calculateHours(entry.checkin, entry.checkout);
    }

    if (entry.checkin && entry.checkout) {
      return { status: 'complete', hours, taskHours, entry };
    } else if (entry.checkin) {
      return { status: 'checked-in', hours: null, taskHours, entry };
    }

    return { status: 'absent', hours: null, taskHours, entry: null };
  };

  // Get status chip
  const getStatusChip = (status: string, hours: number | null, taskHours: number = 0) => {
    const hasTaskHours = taskHours > 0;
    let label = '';
    
    switch (status) {
      case 'complete':
        if (hours !== null) {
          label = hasTaskHours ? `${hours}h / ${taskHours.toFixed(1)}t` : `${hours}h`;
        } else {
          label = hasTaskHours ? `Complete / ${taskHours.toFixed(1)}t` : 'Complete';
        }
        return (
          <Chip 
            label={label} 
            color="success" 
            size="small" 
          />
        );
      case 'checked-in':
        label = hasTaskHours ? `In / ${taskHours.toFixed(1)}t` : 'In';
        return <Chip label={label} color="warning" size="small" />;
      case 'absent':
        label = hasTaskHours ? `${taskHours.toFixed(1)}t` : '-';
        return <Chip label={label} color={hasTaskHours ? "info" : "default"} size="small" />;
      default:
        label = hasTaskHours ? `${taskHours.toFixed(1)}t` : '-';
        return <Chip label={label} color={hasTaskHours ? "info" : "default"} size="small" />;
    }
  };

  const handleTaskHoursClick = (userId: number, date: Date) => {
    setSelectedUserId(userId);
    setSelectedDate(formatDateLocal(date));
    setTaskLogDialogOpen(true);
  };

  const handleTaskLogDialogClose = () => {
    setTaskLogDialogOpen(false);
    setSelectedUserId(null);
    setSelectedDate(null);
  };

  const handleTaskLogSave = () => {
    // Reload attendance data to refresh task hours
    // This will be handled by the parent component's date range change
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Week Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={handlePreviousWeek}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ cursor: 'pointer' }} onClick={handleToday}>
          {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
          {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Typography>
        <IconButton onClick={handleNextWeek}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: { xs: 100, sm: 200 }, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper' }}>
                User
              </TableCell>
              {weekDates.map((date) => (
                <TableCell key={date.toISOString()} align="center" sx={{ minWidth: 120 }}>
                  <Box>
                    <Typography variant="caption" display="block">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => {
              const userAttendance = weekDates.map((date) => getAttendanceStatus(user.id, date));
              const totalHours = userAttendance.reduce((sum, att) => sum + (att.hours || 0), 0);
              const totalTaskHours = userAttendance.reduce((sum, att) => sum + (att.taskHours || 0), 0);

              return (
                <TableRow key={user.id} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      fontWeight: 'medium',
                      maxWidth: { xs: 100, sm: 200 },
                      width: { xs: 100, sm: 200 },
                    }}
                  >
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {user.username || user.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {user.email}
                      </Typography>
                      {(totalHours > 0 || totalTaskHours > 0) && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {totalHours > 0 && formatHoursMinutes(totalHours)}
                          {totalHours > 0 && totalTaskHours > 0 && ' / '}
                          {totalTaskHours > 0 && `${formatHoursMinutes(totalTaskHours)} t`}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {weekDates.map((date, idx) => {
                    const att = userAttendance[idx];
                    const handleCellClick = () => {
                      if (!canManage) return;
                      if (att.entry) {
                        onEdit?.(att.entry);
                      } else {
                        onCreate?.(user, date);
                      }
                    };
                    const tooltipText = att.entry
                      ? `Check-in: ${att.entry.checkin ? new Date(att.entry.checkin).toLocaleTimeString() : 'N/A'}\nCheck-out: ${att.entry.checkout ? new Date(att.entry.checkout).toLocaleTimeString() : 'N/A'}${att.taskHours > 0 ? `\nTask time: ${att.taskHours.toFixed(1)}h${canManage ? '\nClick task hours to view/edit' : ''}` : ''}\n${canManage ? 'Click to edit attendance' : ''}`
                      : att.taskHours > 0
                      ? `Task time: ${att.taskHours.toFixed(1)}h${canManage ? '\nClick task hours to view/edit' : ''}`
                      : canManage ? 'Click to add attendance' : 'No attendance record';
                    return (
                      <TableCell 
                        key={date.toISOString()} 
                        align="center"
                        sx={{ 
                          cursor: canManage ? 'pointer' : 'default',
                          '&:hover': canManage ? { backgroundColor: 'action.hover' } : {},
                        }}
                        onClick={handleCellClick}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title={tooltipText}>
                            <Box
                              onClick={(e) => {
                                if (att.taskHours > 0) {
                                  e.stopPropagation();
                                  handleTaskHoursClick(user.id, date);
                                }
                              }}
                              sx={att.taskHours > 0 ? { cursor: 'pointer' } : {}}
                            >
                              {getStatusChip(att.status, att.hours, att.taskHours)}
                            </Box>
                          </Tooltip>
                          {canManage && att.entry && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit?.(att.entry!);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete?.(att.entry!.id, att.entry!.log_type);
                                }}
                                sx={{ p: 0.5, color: 'error.main' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Task Work Log Dialog */}
      {selectedUserId && selectedDate && (
        <TaskWorkLogDialog
          open={taskLogDialogOpen}
          onClose={handleTaskLogDialogClose}
          userId={selectedUserId}
          date={selectedDate}
          onSave={handleTaskLogSave}
        />
      )}
    </Box>
  );
}
